/**
 * TechDocs alpha frontend plugin registers route refs (e.g. docRoot) used by
 * EntityListDocsTable on the docs index. Legacy FlatRoutes for /docs break that.
 * We install the plugin and override:
 * - reader page → IronPhoenix standalone /docs reader
 * - catalog entity TECHDOCS tab → GatedEntityTechdocsContent (not upstream
 *   EmbeddedDocsRouter / TechDocsReaderPageContent), so gated state UI matches /docs.
 */

import { createElement } from 'react';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import {
  PageBlueprint,
  createExtensionInput,
  coreExtensionData,
  type FrontendFeature,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  AddonBlueprint,
  attachTechDocsAddonComponentData,
} from '@backstage/plugin-techdocs-react/alpha';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { compatWrapper } from '@backstage/core-compat-api';

export const techdocsAppFeature: FrontendFeature = techdocsPlugin.withOverrides({
  extensions: [
    PageBlueprint.makeWithOverrides({
      name: 'reader',
      factory(originalFactory) {
        return originalFactory({
          path: '/docs/:namespace/:kind/:name',
          routeRef: techdocsPlugin.routes.docRoot,
          loader: () =>
            import('./components/techdocs/IronPhoenixTechDocsReaderPage').then(
              m =>
                Promise.resolve(
                  compatWrapper(
                    createElement(m.IronPhoenixTechDocsReaderPage),
                  ),
                ),
            ),
        });
      },
    }),
    EntityContentBlueprint.makeWithOverrides({
      inputs: {
        addons: createExtensionInput([AddonBlueprint.dataRefs.addon]),
        emptyState: createExtensionInput(
          [coreExtensionData.reactElement.optional()],
          { singleton: true, optional: true },
        ),
      },
      factory(originalFactory, context) {
        return originalFactory(
          {
            path: 'docs',
            title: 'TechDocs',
            routeRef: techdocsPlugin.routes.entityContent,
            loader: () =>
              import('./components/techdocs/GatedEntityTechdocsContent').then(
                m => {
                  const addons = context.inputs.addons.map(output => {
                    const options = output.get(AddonBlueprint.dataRefs.addon);
                    const Addon = options.component;
                    attachTechDocsAddonComponentData(Addon, options);
                    return createElement(Addon, { key: options.name });
                  });
                  return compatWrapper(
                    createElement(
                      m.GatedEntityTechdocsContent,
                      undefined,
                      createElement(
                        TechDocsAddons,
                        undefined,
                        addons,
                      ),
                    ),
                  );
                },
              ),
          },
          context,
        );
      },
    }),
  ],
});
